import { useEffect, useMemo, useRef, useState } from 'react';
import { BADGE_DROPDOWN_MASK_BY_TYPE } from './avatarDesignerBadgeOptionMasks';
import { BadgeGlyph } from './AvatarDesignerBadgeGlyph';
import { ZODIAC_BADGE_OPTIONS } from './avatarDesignerZodiac';
import {
  API,
  AvatarFallback,
  DEFAULT_AVATAR_DESIGNER,
  normalizeHexTo6,
  parseAvatarDesignerFromApi,
  toColorInputValue,
} from './profileHelpers';

const BADGE_OPTIONS_BASE = [
  { value: 'none', label: 'Ei mitään' },
  { value: 'pallo', label: 'Pallo' },
  { value: 'crown', label: 'Crown' },
  { value: 'p', label: 'P' },
  { value: 'clock4', label: 'Clock-4' },
  { value: 'eye', label: 'Eye' },
  { value: 'user', label: 'User' },
];

const SLOT_COUNT = 5;
const PREVIEW_SIZE = 220;
const SLOT_RADIUS = 92;

const DROPDOWN_PREVIEW_ICON = 'e8e6e0';

function badgeMenuOptionClasses(value) {
  const base = 'AvatarDesigner-menuOption';
  if (value === 'none') return `${base} AvatarDesigner-menuOption--none`;
  if (value === 'pallo') return `${base} AvatarDesigner-menuOption--pallo`;
  if (value === 'p') return `${base} AvatarDesigner-menuOption--letterP`;
  return `${base} AvatarDesigner-menuOption--mask`;
}

function badgeMenuOptionStyle(value) {
  const mask = BADGE_DROPDOWN_MASK_BY_TYPE[value];
  if (!mask) return undefined;
  return { '--ad-mask': mask };
}

function slotCenterPx(index) {
  const deg = -90 + index * (360 / SLOT_COUNT);
  const rad = (deg * Math.PI) / 180;
  const cx = PREVIEW_SIZE / 2;
  const cy = PREVIEW_SIZE / 2;
  return {
    x: cx + SLOT_RADIUS * Math.cos(rad),
    y: cy + SLOT_RADIUS * Math.sin(rad),
  };
}

export default function AvatarDesignerModal({
  open,
  onClose,
  userId,
  initialDesigner,
  avatarUrl,
  avatarImageError,
  displayName,
  username,
  bubbleColorHex,
  onSaved,
  hasHoroscopeTeemapaketti = false,
}) {
  const [designer, setDesigner] = useState(() => parseAvatarDesignerFromApi(initialDesigner));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [badgeMenuOpen, setBadgeMenuOpen] = useState(false);
  const badgeSelectWrapRef = useRef(null);

  useEffect(() => {
    if (open) {
      let next = parseAvatarDesignerFromApi(initialDesigner);
      if (
        !hasHoroscopeTeemapaketti &&
        typeof next.badgeType === 'string' &&
        next.badgeType.startsWith('zodiac-')
      ) {
        next = { ...next, badgeType: 'none' };
      }
      setDesigner(next);
      setSaveError(null);
    }
  }, [open, initialDesigner, hasHoroscopeTeemapaketti]);

  useEffect(() => {
    if (open) setBadgeMenuOpen(false);
  }, [open]);

  useEffect(() => {
    if (!badgeMenuOpen || typeof document === 'undefined') return undefined;
    const onDocMouseDown = (e) => {
      const el = badgeSelectWrapRef.current;
      if (el && !el.contains(e.target)) setBadgeMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setBadgeMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [badgeMenuOpen]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const showBadge = designer.badgeType !== 'none';
  const slotCenters = useMemo(
    () => Array.from({ length: SLOT_COUNT }, (_, i) => slotCenterPx(i)),
    []
  );

  const badgeLabelByValue = useMemo(() => {
    const m = new Map();
    BADGE_OPTIONS_BASE.forEach((o) => m.set(o.value, o.label));
    ZODIAC_BADGE_OPTIONS.forEach((o) => m.set(o.value, o.label));
    return m;
  }, []);

  const currentBadgeLabel =
    badgeLabelByValue.get(designer.badgeType) ?? designer.badgeType;

  const handleOk = async () => {
    if (!userId) return;
    setSaving(true);
    setSaveError(null);
    const uid = String(userId).replace(/n$/, '');
    const payload = {
      badgeType: designer.badgeType,
      positionIndex: designer.positionIndex,
      iconColor: designer.iconColor,
      badgeBackground: designer.badgeBackground,
    };
    try {
      const res = await fetch(`${API}/api/users/${encodeURIComponent(uid)}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avatarDesigner: payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || data.message || 'Tallennus epäonnistui.');
        return;
      }
      if (typeof onSaved === 'function') {
        onSaved(parseAvatarDesignerFromApi(data.avatarDesigner ?? payload));
      }
      onClose();
    } catch {
      setSaveError('Yhteysvirhe.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const iconHex = normalizeHexTo6(designer.iconColor) || DEFAULT_AVATAR_DESIGNER.iconColor;
  const bgHex = normalizeHexTo6(designer.badgeBackground) || DEFAULT_AVATAR_DESIGNER.badgeBackground;

  return (
    <div className="AvatarDesigner-overlay" role="dialog" aria-modal="true" aria-labelledby="avatar-designer-title">
      <div className="AvatarDesigner-modal">
        <header className="AvatarDesigner-header">
          <h1 id="avatar-designer-title" className="AvatarDesigner-title">
            AVATAR DESIGNER
          </h1>
        </header>

        <div className="AvatarDesigner-body">
          <div className="AvatarDesigner-controls">
            <label
              className="AvatarDesigner-label"
              id="avatar-designer-badge-label"
              htmlFor="avatar-designer-badge"
            >
              Ikoni
            </label>
            <div className="AvatarDesigner-selectWrap" ref={badgeSelectWrapRef}>
              <button
                type="button"
                id="avatar-designer-badge"
                className="AvatarDesigner-select AvatarDesigner-selectTrigger"
                aria-haspopup="listbox"
                aria-expanded={badgeMenuOpen}
                aria-controls="avatar-designer-badge-listbox"
                onClick={() => setBadgeMenuOpen((v) => !v)}
              >
                <span className="AvatarDesigner-selectTrigger-main">
                  {designer.badgeType !== 'none' ? (
                    <span className="AvatarDesigner-selectTrigger-glyph" aria-hidden>
                      <BadgeGlyph
                        badgeType={designer.badgeType}
                        iconColor={DROPDOWN_PREVIEW_ICON}
                        size={18}
                      />
                    </span>
                  ) : null}
                  <span className="AvatarDesigner-selectTrigger-label">{currentBadgeLabel}</span>
                </span>
                <span className="AvatarDesigner-selectTrigger-chevron" aria-hidden>
                  ▾
                </span>
              </button>
              {badgeMenuOpen ? (
                <div
                  id="avatar-designer-badge-listbox"
                  className="AvatarDesigner-menu"
                  role="listbox"
                  aria-labelledby="avatar-designer-badge-label"
                >
                  {BADGE_OPTIONS_BASE.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      aria-selected={designer.badgeType === o.value}
                      className={badgeMenuOptionClasses(o.value)}
                      style={badgeMenuOptionStyle(o.value)}
                      onClick={() => {
                        setDesigner((d) => ({ ...d, badgeType: o.value }));
                        setBadgeMenuOpen(false);
                      }}
                    >
                      {o.label}
                    </button>
                  ))}
                  {hasHoroscopeTeemapaketti ? (
                    <>
                      <div className="AvatarDesigner-menuGroupLabel" aria-hidden>
                        Horoskoopit
                      </div>
                      {ZODIAC_BADGE_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          role="option"
                          aria-selected={designer.badgeType === o.value}
                          className={badgeMenuOptionClasses(o.value)}
                          style={badgeMenuOptionStyle(o.value)}
                          onClick={() => {
                            setDesigner((d) => ({ ...d, badgeType: o.value }));
                            setBadgeMenuOpen(false);
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>

            {showBadge && (
              <>
                <label className="AvatarDesigner-label" htmlFor="avatar-designer-icon-color">
                  Ikonin väri
                </label>
                <div className="AvatarDesigner-color-row">
                  <input
                    id="avatar-designer-icon-color"
                    type="color"
                    value={toColorInputValue(iconHex)}
                    onChange={(e) => {
                      const h = normalizeHexTo6(e.target.value) || iconHex;
                      setDesigner((d) => ({ ...d, iconColor: h }));
                    }}
                    className="AvatarDesigner-color-input"
                  />
                  <span className="AvatarDesigner-color-hex">#{iconHex}</span>
                </div>

                <label className="AvatarDesigner-label" htmlFor="avatar-designer-badge-bg">
                  Taustan väri
                </label>
                <div className="AvatarDesigner-color-row">
                  <input
                    id="avatar-designer-badge-bg"
                    type="color"
                    value={toColorInputValue(bgHex)}
                    onChange={(e) => {
                      const h = normalizeHexTo6(e.target.value) || bgHex;
                      setDesigner((d) => ({ ...d, badgeBackground: h }));
                    }}
                    className="AvatarDesigner-color-input"
                  />
                  <span className="AvatarDesigner-color-hex">#{bgHex}</span>
                </div>
              </>
            )}
          </div>

          <div className="AvatarDesigner-preview-wrap">
            <p className="AvatarDesigner-preview-hint">
              {showBadge
                ? 'Valitse merkin paikka renkaalla. Merkki näkyy vain valitussa kohdassa.'
                : 'Valitse ensin ikoni pudotusvalikosta.'}
            </p>
            <div
              className="AvatarDesigner-ring"
              style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
            >
              <div className="AvatarDesigner-avatar-inner">
                {avatarUrl && !avatarImageError ? (
                  <img src={avatarUrl} alt="" className="AvatarDesigner-avatar-img" />
                ) : (
                  <AvatarFallback
                    bubbleColorHex={bubbleColorHex}
                    displayName={displayName}
                    username={username}
                    size="large"
                    className="AvatarDesigner-avatar-fallback"
                  />
                )}
              </div>

              {showBadge &&
                slotCenters.map((pos, i) => {
                  const selected = designer.positionIndex === i;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`AvatarDesigner-slot${
                        selected
                          ? ' AvatarDesigner-slot--badge AvatarDesigner-slot--active'
                          : ' AvatarDesigner-slot--marker'
                      }`}
                      style={{
                        left: pos.x,
                        top: pos.y,
                        ...(selected ? { backgroundColor: `#${bgHex}` } : undefined),
                      }}
                      onClick={() => setDesigner((d) => ({ ...d, positionIndex: i }))}
                      aria-label={`Merkin paikka ${i + 1}`}
                      aria-pressed={selected}
                    >
                      {selected && (
                        <span className="AvatarDesigner-slot-glyph">
                          <BadgeGlyph badgeType={designer.badgeType} iconColor={iconHex} size={20} />
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>

        {saveError && <p className="AvatarDesigner-error">{saveError}</p>}

        <footer className="AvatarDesigner-footer">
          <button
            type="button"
            className="Button AvatarDesigner-ok"
            onClick={handleOk}
            disabled={saving}
          >
            {saving ? 'Tallennetaan…' : 'OK'}
          </button>
        </footer>
      </div>
    </div>
  );
}
